import { Spacing } from '@persona-id/design-tokens';
import { CircleHelpIcon } from '@persona-id/design-tokens/icons';
import { useMemo } from 'react';
import { Control, Controller, FieldErrors, UseFormReturn } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import { DisabledFeatureConfigPanelForm } from 'dashboard/apps/VerificationTemplateEditor/DisabledFeatureConfigPanel/util';
import ReactSelect from 'dashboard/components/ReactSelect';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { GovernmentIdCoverage } from 'dashboard/models/government-id-coverage';
import {
  FeaturePropertiesMap,
  getFeaturesForVtv,
} from 'dashboard/models/verification-template-version';
import { setGovernmentIdCoverage } from 'dashboard/redux/actions/government-id-coverage';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { ErrorText } from 'design-system/components/FormControl';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import {
  GOVERNMENT_ID_CLASSES,
  GOVERNMENT_ID_DISPLAY_STRINGS,
  GovernmentIdClass,
} from 'lib/constants/governmentIds';
import { CA_PROVINCES, COUNTRIES, CountryCode, US_STATES } from 'lib/constants/locales';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';

const Section = styled.section`
  padding: ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const StyledLabel = styled(Label)`
  margin-bottom: ${Spacing[2]};
  display: inline-block;
`;

const FieldErrorText = ({ message }: { message?: string }) =>
  message ? (
    <>
      <Spacer height={0.5} />
      <ErrorText>{message}</ErrorText>
    </>
  ) : null;

interface SubdivisionSectionProps {
  label: string;
  placeholder: string;
  options: Record<string, string>;
  control: Control<DisabledFeatureConfigPanelForm, object>;
  errors: FieldErrors<DisabledFeatureConfigPanelForm>;
}

const SubdivisionSection = ({
  label,
  placeholder,
  options,
  control,
  errors,
}: SubdivisionSectionProps) => {
  return (
    <Section>
      <Spacer height={1.5} />
      <StyledLabel>{label}</StyledLabel>
      <Controller
        name='subdivision'
        control={control}
        render={({ field }) => (
          <FieldWrapper>
            <Select {...field} placeholder={placeholder}>
              <Option key='default' value='default' subtitle={`All ${label}s`}>
                All {label}s
              </Option>
              {Object.keys(options).map((key) => (
                <Option key={key} value={key} subtitle={key}>
                  {options[key as keyof typeof options]}
                </Option>
              ))}
            </Select>
            {field.value === 'QC' && (
              <Paragraph>
                Please ensure you are compliant with local regulations before you enable biometrics
                processing and/or storage in this jurisdiction.
              </Paragraph>
            )}
          </FieldWrapper>
        )}
      />
      <FieldErrorText message={errors?.subdivision?.message} />
    </Section>
  );
};

interface DisabledFeatureConfigPanelContentProps {
  vtvType: ApiModel.VerificationTemplateVersionGovernmentId;
  formReturn: UseFormReturn<DisabledFeatureConfigPanelForm, object>;
}

const DisabledFeatureConfigPanelContent = ({
  vtvType,
  formReturn,
}: DisabledFeatureConfigPanelContentProps) => {
  const { register, formState, watch, control } = formReturn;
  const { errors } = formState;

  const governmentIdCoverage = useSelector((state: RootState) => {
    return state.governmentIdCoverage.governmentIdCoverage;
  });

  const dispatch = useDispatch();
  useApiLoader({
    loader: async () => {
      const coverageResponse = await SessionClientDashboard.get<GovernmentIdCoverage>(
        ApiEndpoint.VerificationGovernmentId,
        'coverage',
        '',
      );

      dispatch(setGovernmentIdCoverage(coverageResponse));
    },
    skip: governmentIdCoverage != null,
    deps: [governmentIdCoverage],
    onError: () =>
      dispatch(showMessage('Failed to load government ID coverage.', MessageType.Failure)),
    onNetworkError: () =>
      dispatch(
        showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
      ),
  });

  const countryCoverages = useMemo(
    () => (governmentIdCoverage ? governmentIdCoverage['country_coverages'] : []),
    [governmentIdCoverage],
  );
  const countryCode = watch('countryCode');

  const governmentIdCountryCoverage = countryCoverages.find((governmentIdCountryCoverage) => {
    return governmentIdCountryCoverage['country_code'] === countryCode;
  });

  let idClasses: GovernmentIdClass[] = Array.from(GOVERNMENT_ID_CLASSES);
  if (governmentIdCountryCoverage) {
    const otherIdCoverages = governmentIdCountryCoverage['other_id_support'] || [];
    const idCoverages = [
      governmentIdCountryCoverage['dl_support'],
      governmentIdCountryCoverage['id_support'],
      governmentIdCountryCoverage['pp_support'],
    ].concat(otherIdCoverages);

    idClasses = idCoverages
      .map((countryCoverage) => countryCoverage?.class)
      .filter((idClass) => idClass);
  }
  return (
    <>
      <Section>
        <StyledLabel>Country</StyledLabel>
        <Controller
          name='countryCode'
          control={control}
          render={({ field }) => (
            <FieldWrapper>
              <Select {...field} placeholder='Select Country'>
                {[
                  <Option key='default' value='default' subtitle='All Countries'>
                    All Countries
                  </Option>,
                  ...Object.keys(COUNTRIES).map((countryCode) => (
                    <Option key={countryCode} value={countryCode} subtitle={countryCode}>
                      {COUNTRIES[countryCode as CountryCode]}
                    </Option>
                  )),
                ]}
              </Select>
            </FieldWrapper>
          )}
        />
        <Spacer height={1.5} />
        <StyledLabel>Country Code</StyledLabel>
        <FieldWrapper>
          <Input {...register('countryCode')} disabled hasError={!!errors?.countryCode} />
        </FieldWrapper>
        <FieldErrorText message={errors?.countryCode?.message} />
      </Section>
      {countryCode === 'CA' && (
        <SubdivisionSection
          label='Province'
          placeholder='Select Province'
          options={CA_PROVINCES}
          control={control}
          errors={errors}
        />
      )}
      {countryCode === 'US' && (
        <SubdivisionSection
          label='State'
          placeholder='Select State'
          options={US_STATES}
          control={control}
          errors={errors}
        />
      )}
      <Section>
        {/* TODO(lucas): Filter by relevant ID Class per country. If ID class isn't present, set idClasses to 'default' */}
        <Spacer height={1.5} />
        <StyledLabel>Targeted ID classes (Optional)</StyledLabel>
        <Controller
          name='idClasses'
          control={control}
          render={({ field }) => (
            <FieldWrapper>
              <ReactSelect
                {...field}
                isMulti
                placeholder='Filter by ID Classes'
                options={idClasses.map((idClass) => ({
                  label: idClass ? GOVERNMENT_ID_DISPLAY_STRINGS[idClass] : '',
                  value: idClass,
                }))}
              ></ReactSelect>
            </FieldWrapper>
          )}
        />
        <FieldErrorText message={errors?.idClasses?.message} />
      </Section>
      <Section>
        {/* TODO(lucas): Filter out non-selectable features */}
        <Spacer height={1.5} />
        <StyledLabel>Disabled Features</StyledLabel>
        <Controller
          name='disabledFeatures'
          control={control}
          render={({ field }) => (
            <FieldWrapper>
              <ReactSelect
                {...field}
                isMulti
                placeholder='Configurable Features'
                options={getFeaturesForVtv(vtvType)
                  .filter((value) => FeaturePropertiesMap[value].configurable)
                  .map((value) => ({
                    label: value,
                    description: FeaturePropertiesMap[value].description,
                    value: value,
                  }))}
                formatOptionLabel={({ label, description }, { context }) =>
                  context === 'menu' ? (
                    <>
                      {label}
                      {/* Only show help icon in dropdown. */}
                      <HelpIcon icon={<CircleHelpIcon />}>{description}</HelpIcon>
                    </>
                  ) : (
                    label
                  )
                }
              />
            </FieldWrapper>
          )}
        />
        <FieldErrorText message={errors?.disabledFeatures?.message} />
      </Section>
    </>
  );
};

export default DisabledFeatureConfigPanelContent;
